import React, { useContext } from 'react';
import { Terminal, Code, Link } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';

const Integration = () => {
  const { user } = useContext(AuthContext);
  
  // Note: Adjust the domain as per your production URL
  const webhookUrl = 'https://rem.avanticapumps.com/api/webhook/trade';

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700/50 overflow-hidden p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">MT5 Integration Guide</h2>
            <p className="text-slate-400">
              Follow these instructions to connect your MetaTrader 5 Expert Advisor to this dashboard.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <a 
              href="/WebhookSender.mq5" 
              download="WebhookSender.mq5"
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-lg shadow-indigo-500/25 whitespace-nowrap"
            >
              <Code className="h-5 w-5" />
              Download Sender EA (Master)
            </a>
            <a 
              href="/WebhookReceiver.mq5" 
              download="WebhookReceiver.mq5"
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-lg shadow-emerald-500/25 whitespace-nowrap"
            >
              <Code className="h-5 w-5" />
              Download Receiver EA (Slave)
            </a>
          </div>
        </div>

        <div className="space-y-8">
          
          {/* Section: Master Setup */}
          <div>
            <h2 className="text-xl font-bold text-indigo-400 mb-4 border-b border-slate-700 pb-2">Master Account Setup (Sender)</h2>
          {/* Step 1 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-indigo-500/10 p-2 rounded-lg text-indigo-400">
                <Link className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-white">1. The Webhook URL</h3>
            </div>
            <p className="text-slate-400 mb-2">Your MT5 EA must send HTTP POST requests to this URL:</p>
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 text-emerald-400 font-mono text-sm break-all">
              {webhookUrl}
            </div>
          </div>

          {/* Step 2 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-indigo-500/10 p-2 rounded-lg text-indigo-400">
                <Code className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-white">2. Payload Structure</h3>
            </div>
            <p className="text-slate-400 mb-2">The JSON payload must include your User ID to link the trades to your account.</p>
            <p className="text-sm font-semibold text-white mb-2">Your User ID: <span className="text-indigo-400">{user?.id || 'Unknown'}</span></p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
                <p className="text-xs text-slate-500 font-bold mb-2 uppercase tracking-wide">For New Trades (Open)</p>
                <pre className="text-xs text-indigo-300 overflow-x-auto font-mono">
{`{
  "user_id": ${user?.id || 1},
  "ticket": "123456",
  "symbol": "EURUSD",
  "type": "Buy",
  "volume": 0.1,
  "open_price": 1.0950,
  "sl": 1.0900,
  "tp": 1.1000,
  "status": "Open",
  "open_time": "2026-07-02T10:00:00Z"
}`}
                </pre>
              </div>
              <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
                <p className="text-xs text-slate-500 font-bold mb-2 uppercase tracking-wide">For Closed Trades</p>
                <pre className="text-xs text-indigo-300 overflow-x-auto font-mono">
{`{
  "user_id": ${user?.id || 1},
  "ticket": "123456",
  "symbol": "EURUSD",
  "type": "Buy",
  "volume": 0.1,
  "open_price": 1.0950,
  "close_price": 1.1000,
  "sl": 1.0900,
  "tp": 1.1000,
  "profit": 50.00,
  "status": "Closed",
  "open_time": "2026-07-02T10:00:00Z",
  "close_time": "2026-07-02T14:00:00Z"
}`}
                </pre>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-indigo-500/10 p-2 rounded-lg text-indigo-400">
                <Terminal className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-white">3. Allow WebRequest in MT5</h3>
            </div>
            <p className="text-slate-400">
              In MetaTrader 5, go to <strong>Tools</strong> &gt; <strong>Options</strong> &gt; <strong>Expert Advisors</strong>. 
              Check <em>"Allow WebRequest for listed URL"</em> and add <code>https://rem.avanticapumps.com</code> to the list. 
              Without this, your EA will be blocked from sending trade data to the backend.
            </p>
          </div>
          </div>

          {/* Section: Slave Setup */}
          <div className="mt-12 pt-8 border-t border-slate-700/50">
            <h2 className="text-xl font-bold text-emerald-400 mb-4 border-b border-slate-700 pb-2">Copy Trading Setup (Receiver)</h2>
            <div className="space-y-6">
              
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-emerald-500/10 p-2 rounded-lg text-emerald-400">
                    <Link className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">1. Configure the Receiver EA</h3>
                </div>
                <p className="text-slate-400 mb-2">
                  Attach the <strong>Receiver EA (Slave)</strong> to a chart on your personal MetaTrader 5 terminal. In the Inputs tab, configure the following:
                </p>
                <ul className="list-disc list-inside text-slate-400 space-y-1 ml-2">
                  <li><strong>InpUserId:</strong> Set this to the Master's User ID (<span className="text-indigo-400">{user?.id || 'Unknown'}</span>).</li>
                  <li><strong>InpWebhookUrl:</strong> <code>https://rem.avanticapumps.com/api/webhook/copy-state</code></li>
                  <li><strong>InpLotMultiplier:</strong> Set your desired lot size multiplier (e.g., 0.5 to copy half the lot size).</li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-emerald-500/10 p-2 rounded-lg text-emerald-400">
                    <Terminal className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">2. Allow WebRequests</h3>
                </div>
                <p className="text-slate-400">
                  Just like the Master bot, ensure your Slave MT5 terminal has <em>"Allow WebRequest for listed URL"</em> enabled for <code>https://rem.avanticapumps.com</code> in the Options &gt; Expert Advisors menu.
                </p>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Integration;
